import React, { useCallback, useMemo, useRef } from "react";
import { PartCategory } from "src/api/generated/erp/db/types/tables/partCategory";
import { Box, Typography } from "@mui/material";
import { PartCategoryApi } from "src/api/generated/erp/parts/categories/partCategoryApi";
import i18n from "i18next";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";
import { useStoredState } from "src/utils/storedState";
import { TreeItem } from "@mui/x-tree-view";
import { AavoTreeView } from "src/components/common/tree/AavoTreeView";
import { useDrag, useDrop } from "react-dnd";
import { columnFlex, flex1 } from "src/styles/sx";
import { mergeSx } from "src/utils/styles";
import { AsyncButtonContextMenuItem } from "src/components/common/contextMenu/AsyncButtonContextMenuItem.tsx";
import { useContextMenu } from "src/components/common/contextMenu/useContextMenu.ts";
import { AavoContextMenu } from "src/components/common/contextMenu/AavoContextMenu.tsx";
import { PartCategoryFormProps } from "src/components/views/erp/parts/partCategories/PartCategoryForm.tsx";

export interface PartCategoryWithChildren extends PartCategory {
	children: PartCategoryWithChildren[];
}

export interface PartCategoriesTreeViewProps {
	partCategories: PartCategory[];
	refreshData: () => Promise<PartCategory[]>;
	selectedPartCategory: PartCategory | null;
	setSelectedPartCategory: React.Dispatch<React.SetStateAction<PartCategory | null>>;
	openPartCategoryFormDialog: (params: Partial<PartCategoryFormProps>) => void;
}

export const PartCategoriesTreeView = ({
	partCategories,
	refreshData,
	setSelectedPartCategory,
	selectedPartCategory,
	openPartCategoryFormDialog,
}: PartCategoriesTreeViewProps) => {
	const [openNodes, setOpenNodes] = useStoredState<string[]>([], "D52E93C2913BDE80");
	const [openContextMenu, contextMenuState] = useContextMenu();

	const rootLevelPartCategories = useMemo(() => {
		return partCategories
			.map((partCategory) => addChildrenToPartCategory(partCategory, partCategories))
			.filter((partCategory) => partCategory.parentId === null);
	}, [partCategories]);

	const onPartCategoryRightClick = (e: React.MouseEvent, partCategory: PartCategory) => {
		e.stopPropagation();
		openContextMenu({
			content: (
				<AsyncButtonContextMenuItem
					label={i18n.t("create_sub_category")}
					icon={faPlus}
					onClick={() => {
						openPartCategoryFormDialog({
							parentId: partCategory.partCategoryId,
						});
					}}
				/>
			),
			mouseEvent: e,
		});
	};

	return (
		<Box sx={mergeSx(columnFlex, flex1)}>
			<AavoTreeView
				multiSelect={false}
				expanded={openNodes}
				onNodeToggleOnIconClick={(_, nodeIds) => {
					setOpenNodes(nodeIds);
				}}
				selected={
					selectedPartCategory === null
						? null
						: getPartCategoryNodeId(selectedPartCategory)
				}
				onNodeSelect={(_: React.SyntheticEvent, nodeId: string) => {
					const newSelectedPartCategory = partCategories.find(
						(partCategory) => getPartCategoryNodeId(partCategory) === nodeId,
					);
					setSelectedPartCategory(newSelectedPartCategory ?? null);
				}}
				sx={{
					width: 200,
				}}
			>
				{rootLevelPartCategories.map((partCategory) => (
					<PartCategoryTreeItem
						key={getPartCategoryNodeId(partCategory)}
						partCategory={partCategory}
						onRightClick={onPartCategoryRightClick}
						refreshData={refreshData}
						expandNode={(nodeId) => {
							setOpenNodes((openNodes) => openNodes.concat(nodeId));
						}}
					/>
				))}
			</AavoTreeView>
			<RootLevelDropArea refreshData={refreshData} />
			<AavoContextMenu state={contextMenuState} />
		</Box>
	);
};

const RootLevelDropArea = ({ refreshData }: { refreshData: () => void }) => {
	const onDrop = async (dropped: PartCategory) => {
		await PartCategoryApi.save({
			partCategory: {
				...dropped,
				parentId: null,
			},
		});
		refreshData();
	};

	const [{ canDrop, isDraggingOver }, dropRef] = useDrop(() => ({
		accept: PART_CATEGORY_DND_TYPE,
		drop: onDrop,
		collect: (monitor) => ({
			canDrop: monitor.canDrop(),
			isDraggingOver: monitor.isOver(),
		}),
	}));

	return (
		<Box
			ref={dropRef}
			sx={{
				flex: 1,
				display: canDrop ? "flex" : "none",
				backgroundColor: (theme) => theme.palette.grey[300],
				opacity: isDraggingOver ? 0.5 : 1,
			}}
		>
			<Typography
				sx={{
					margin: "auto",
					fontSize: "1.5rem",
				}}
			>
				{i18n.t("drop_to_root_level")}
			</Typography>
		</Box>
	);
};

interface PartCategoryTreeItemProps {
	partCategory: PartCategoryWithChildren;
	onRightClick: (e: React.MouseEvent, partCategory: PartCategory) => void;
	refreshData: () => void;
	expandNode: (nodeId: string) => void;
}

const PartCategoryTreeItem = ({
	partCategory,
	onRightClick,
	refreshData,
	expandNode,
}: PartCategoryTreeItemProps) => {
	const labelRef = useRef(null);

	const [{ isDragging }, drag] = useDrag(() => ({
		type: PART_CATEGORY_DND_TYPE,
		item: partCategory,
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	}));

	const onDropToThis = async (dropped: PartCategory) => {
		await PartCategoryApi.save({
			partCategory: {
				...dropped,
				parentId: partCategory.partCategoryId,
			},
		});
		refreshData();
		expandNode(getPartCategoryNodeId(partCategory));
	};

	const [{ canDrop, isDraggingOver }, drop] = useDrop(() => ({
		accept: PART_CATEGORY_DND_TYPE,
		drop: onDropToThis,
		collect: (monitor) => ({
			canDrop: monitor.canDrop(),
			isDraggingOver: monitor.isOver(),
		}),
	}));

	drag(drop(labelRef));

	const treeItemRef = useCallback((elt: HTMLLIElement) => {
		elt?.addEventListener("focusin", (e) => {
			// Disable TreeView focus system which make draggable on TreeItem unusable
			// see https://github.com/mui-org/material-ui/issues/29518
			e.stopImmediatePropagation();
		});
	}, []);

	const label = (
		<Typography
			ref={labelRef}
			sx={{
				opacity: isDragging || (canDrop && isDraggingOver) ? 0.5 : 1,
				display: "flex",
				gap: "0.5rem",
				alignItems: "center",
				minHeight: "2.5rem",
				marginY: "0.1rem",
			}}
		>
			{partCategory.categoryName}
		</Typography>
	);

	return (
		<TreeItem
			ref={treeItemRef}
			nodeId={getPartCategoryNodeId(partCategory)}
			label={label}
			onContextMenu={(e) => onRightClick(e, partCategory)}
		>
			{partCategory.children.map((child) => (
				<PartCategoryTreeItem
					key={getPartCategoryNodeId(child)}
					partCategory={child}
					onRightClick={onRightClick}
					refreshData={refreshData}
					expandNode={expandNode}
				/>
			))}
		</TreeItem>
	);
};

const addChildrenToPartCategory = (
	partCategory: PartCategory,
	allCategories: PartCategory[],
): PartCategoryWithChildren => {
	return {
		...partCategory,
		children: allCategories
			.filter((child) => child.parentId === partCategory.partCategoryId)
			.map((child) => addChildrenToPartCategory(child, allCategories)),
	};
};

const getPartCategoryNodeId = (partCategory: PartCategory): string =>
	partCategory.partCategoryId.toString();

const PART_CATEGORY_DND_TYPE = "PART_CATEGORY";
